import { useCallback, useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import IVSBroadcastClient from "amazon-ivs-web-broadcast";
import { initLiveStream, saveMetadata, setPreview, startLive, endLive, getStreamStatus } from "../components/services/liveService";
import { getStoredToken, getUser } from "../utils/auth";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const SOCKET_URL = API_BASE_URL.replace(/\/api$/i, "");
const PIP_SCALE = 0.3;
const PIP_MARGIN = 0.02;
const PAUSED_IMAGE = "paused";
// The live of this tab, so a reload can resume broadcasting to it (the stream key is fetched again, never stored)
const ACTIVE_LIVE_KEY = "activeLive";
const RESUMABLE_STATUSES = ["live", "paused"];

const storeActiveLive = (value) => {
  try {
    if (value) sessionStorage.setItem(ACTIVE_LIVE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(ACTIVE_LIVE_KEY);
  } catch (error) {
    console.warn("Unable to remember the active live:", error);
  }
};

// Positions on the broadcast canvas: the host first, then one slot per guest (see SPLIT_LAYOUTS in useLiveStage)
const layoutSlots = (layout, count, { width, height }) => {
  if (layout === "pip") {
    const w = Math.round(width * PIP_SCALE);
    const h = Math.round(height * PIP_SCALE);
    const m = Math.round(width * PIP_MARGIN);
    return [
      { x: 0, y: 0, width, height },
      ...Array.from({ length: count - 1 }, (_, i) => ({ x: width - (i + 1) * (w + m), y: height - h - m, width: w, height: h })),
    ];
  }
  return Array.from({ length: count }, (_, i) =>
    layout === "horizontal_split"
      ? { x: 0, y: Math.round((i * height) / count), width, height: Math.round(height / count) }
      : { x: Math.round((i * width) / count), y: 0, width: Math.round(width / count), height }
  );
};

const pausedFrame = ({ width, height }) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#222222";
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "center";
  ctx.font = `600 ${Math.round(height / 9)}px sans-serif`;
  ctx.fillText("Live Paused.", width / 2, height / 2);
  ctx.font = `${Math.round(height / 26)}px sans-serif`;
  ctx.fillText("The Host Will Resume Shortly", width / 2, height / 2 + height / 10);
  return canvas;
};

/**
 * Host side of a live (`streamType` from /live/init): camera preview, IVS broadcast, the live lifecycle and the
 * viewer count. Leaving the page ends the live, since nothing is broadcast once the page is gone; a reload doesn't
 * run that cleanup, so the live is offered back as `pendingLive` to resume or end. `endedLive` describes the last
 * live that ended, by the host or by the server (auto-end, admin), with `byServer` set in the latter case.
 * `sessionLimit` is when the host plan's time for the live runs out (the server then ends it).
 */
const useLiveBroadcast = (streamType) => {
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState(false);
  const [live, setLive] = useState(null); // { streamId, startedAt }
  const [viewerCount, setViewerCount] = useState(0);
  const [socket, setSocket] = useState(null);
  const [pendingLive, setPendingLive] = useState(null); // stream status of a live left running by a reload
  const [endedLive, setEndedLive] = useState(null); // { streamId, startedAt, endedAt, byServer }
  // The host plan's time for this live: { minutes, endsAt }; null while paused (paused time isn't counted)
  const [sessionLimit, setSessionLimit] = useState(null);
  const broadcastRef = useRef(null);
  const mountedRef = useRef(false);
  const stoppingRef = useRef(false);
  const liveRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const composedRef = useRef(new Map()); // mixer source name -> what it currently shows
  const composeQueueRef = useRef(Promise.resolve());

  const startCamera = useCallback(async () => {
    setCameraError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        // The best the camera offers up to full HD; the broadcast scales it to what the host's plan allows
        video: { width: { ideal: 1920 }, height: { ideal: 1080 }, facingMode: "user" },
        audio: true,
      });
      // The permission prompt can outlive the page, and a newer request replaces an older stream
      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = stream;
      setCameraStream(stream);
    } catch (error) {
      console.error("Error accessing camera/microphone:", error);
      setCameraError(true);
    }
  }, []);

  const stopBroadcast = () => {
    try {
      broadcastRef.current?.stopBroadcast();
      broadcastRef.current?.delete();
    } catch (error) {
      console.error("Error stopping broadcast:", error);
    }
    broadcastRef.current = null;
    composedRef.current.clear();
  };

  useEffect(() => {
    mountedRef.current = true;
    startCamera();
    return () => {
      mountedRef.current = false;
      stopBroadcast();
      cameraStreamRef.current?.getTracks().forEach((track) => track.stop());
      if (liveRef.current) {
        storeActiveLive(null);
        endLive(liveRef.current.streamId).catch(() => {});
      }
    };
  }, [startCamera]);

  useEffect(() => {
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(ACTIVE_LIVE_KEY));
    } catch (error) {
      saved = null;
    }
    if (saved?.streamType !== streamType) return;
    let active = true;
    getStreamStatus(saved.streamId)
      .then((status) => {
        if (!active) return;
        if (RESUMABLE_STATUSES.includes(status.status) && status.stream_key) {
          setPendingLive({
            ...status,
            ingestEndpoint: saved.ingestEndpoint || status.channel_status?.ingest_endpoint,
            hd: saved.hd,
          });
        } else {
          storeActiveLive(null);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [streamType]);

  useEffect(() => {
    liveRef.current = live;
  }, [live]);

  // The server sends the time left as seconds, so a wrong clock on the host's device can't shift the countdown
  const refreshSessionLimit = useCallback((id) => {
    getStreamStatus(id)
      .then((status) => {
        if (liveRef.current?.streamId !== id) return;
        const seconds = status.session_seconds_left;
        setSessionLimit(
          status.status === "live" && seconds !== null && seconds !== undefined && Number.isFinite(Number(seconds))
            ? { minutes: status.session_duration_limit_minutes, endsAt: Date.now() + Number(seconds) * 1000 }
            : null
        );
      })
      .catch(() => {});
  }, []);

  const streamId = live?.streamId;
  useEffect(() => {
    if (!streamId) return;
    const userId = getUser()?.id;
    const s = io(SOCKET_URL, { transports: ["websocket"], auth: { token: getStoredToken() } });
    // Rooms belong to the connection, so they are joined again after a reconnect
    s.on("connect", () => {
      s.emit("join-stream", { streamId, userId });
      refreshSessionLimit(streamId);
    });
    s.on("viewer-count-updated", (data) => setViewerCount(data.viewerCount || 0));
    // Ended by the server (auto-end, admin): stop broadcasting like Stop does, without ending it again
    const onEnded = () => {
      const ended = liveRef.current;
      if (stoppingRef.current || ended?.streamId !== streamId) return;
      liveRef.current = null;
      stopBroadcast();
      storeActiveLive(null);
      setEndedLive({ ...ended, endedAt: Date.now(), byServer: true });
      setLive(null);
      setViewerCount(0);
    };
    s.on("stream-ended", onEnded);
    s.on("live-status-updated", (data) => {
      const status = String(data.status).toUpperCase();
      if (status === "ENDED") onEnded();
      else if (status === "PAUSED") setSessionLimit(null);
      else if (status === "LIVE") refreshSessionLimit(streamId);
    });
    setSocket(s);
    return () => {
      s.emit("leave-stream", { streamId, userId });
      s.disconnect();
      setSocket(null);
      setSessionLimit(null);
    };
  }, [streamId, refreshSessionLimit]);

  useEffect(() => {
    if (!streamId) return;
    const warnBeforeLeaving = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeLeaving);
    return () => window.removeEventListener("beforeunload", warnBeforeLeaving);
  }, [streamId]);

  // The host's plan decides the quality: HD up to 1080p, otherwise 480p (lives started before this flag default to HD)
  const startBroadcast = async (streamKey, ingestEndpoint, hd = true) => {
    const client = IVSBroadcastClient.create({
      streamConfig: hd ? IVSBroadcastClient.STANDARD_LANDSCAPE : IVSBroadcastClient.BASIC_LANDSCAPE,
    });
    broadcastRef.current = client;
    await client.addVideoInputDevice(new MediaStream(cameraStream.getVideoTracks()), "camera", { index: 0 });
    if (cameraStream.getAudioTracks().length) {
      await client.addAudioInputDevice(new MediaStream(cameraStream.getAudioTracks()), "microphone");
    }
    await client.startBroadcast(streamKey, ingestEndpoint);
  };

  /**
   * init -> configure -> metadata -> preview -> IVS broadcast -> start.
   * A failure after init ends the half-created live and is re-thrown for the page to show.
   */
  const goLive = async ({ init, configuration, metadata }) => {
    if (!cameraStream) throw new Error("Allow camera access to go live");

    let newStreamId = null;
    try {
      const stream = await initLiveStream({ ...init, distroy_previous_stream: true });
      newStreamId = stream.stream_id;
      if (!stream.stream_key || !stream.ingest_endpoint) throw new Error("Streaming channel is not available");

      const configRes = await fetch(`${API_BASE_URL}/live/configure`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token: getStoredToken() },
        body: JSON.stringify({ stream_id: newStreamId, ...configuration }),
      });
      if (!configRes.ok) throw new Error((await configRes.json()).message || "Unable to configure the live");

      await saveMetadata({ stream_id: newStreamId, ...metadata });
      await setPreview(newStreamId);

      const hd = stream.hd_streaming_enabled !== false;
      await startBroadcast(stream.stream_key, stream.ingest_endpoint, hd);

      await startLive(newStreamId);
      storeActiveLive({ streamId: newStreamId, streamType, ingestEndpoint: stream.ingest_endpoint, hd });
      setPendingLive(null);
      setEndedLive(null);
      setLive({ streamId: newStreamId, startedAt: new Date() });
      return newStreamId;
    } catch (error) {
      stopBroadcast();
      if (newStreamId) endLive(newStreamId).catch(() => {});
      throw error;
    }
  };

  // The live is only cleared once the API has ended it, so a failed request can be retried
  const stopLive = async () => {
    const current = liveRef.current;
    if (!current) return;
    stoppingRef.current = true;
    try {
      await endLive(current.streamId);
    } finally {
      stoppingRef.current = false;
    }
    // The server's own stream-ended for this stop can arrive after the response
    liveRef.current = null;
    stopBroadcast();
    storeActiveLive(null);
    setEndedLive({ ...current, endedAt: Date.now(), byServer: false });
    setLive(null);
    setViewerCount(0);
  };

  /** Broadcasts to the live found after a reload again; returns its stream status for the page to restore. */
  const resumeLive = async () => {
    if (!cameraStream) throw new Error("Allow camera access to go live");
    if (!pendingLive.ingestEndpoint) throw new Error("Streaming channel is not available");
    try {
      await startBroadcast(pendingLive.stream_key, pendingLive.ingestEndpoint, pendingLive.hd !== false);
    } catch (error) {
      stopBroadcast();
      throw error;
    }
    setLive({ streamId: pendingLive.stream_id, startedAt: new Date(pendingLive.started_at) });
    setPendingLive(null);
    setEndedLive(null);
    return pendingLive;
  };

  const discardLive = async () => {
    const ended = { streamId: pendingLive.stream_id, startedAt: pendingLive.started_at };
    await endLive(ended.streamId);
    setPendingLive(null);
    storeActiveLive(null);
    setEndedLive({ ...ended, endedAt: Date.now(), byServer: false });
  };

  /**
   * Puts stage guests ({ userId, videoStream, audioStream, muted }) into the broadcast next to the host's camera and
   * covers the host's slot while paused; a muted guest's audio is left out. Calls are queued because adding mixer
   * sources is asynchronous.
   */
  const composeStage = useCallback((layout, guests, paused) => {
    composeQueueRef.current = composeQueueRef.current
      .then(async () => {
        const client = broadcastRef.current;
        if (!client) return;
        const composed = composedRef.current;
        const slots = layoutSlots(layout, guests.length + 1, client.getCanvasDimensions());
        client.updateVideoDeviceComposition("camera", { index: 0, ...slots[0] });

        const wanted = new Set([PAUSED_IMAGE]);
        for (const [i, guest] of guests.entries()) {
          const videoName = `guest-video-${guest.userId}`;
          const audioName = `guest-audio-${guest.userId}`;
          const position = { index: i + 2, ...slots[i + 1] };
          const videoTrack = guest.videoStream?.getVideoTracks()[0];
          const audioTrack = guest.muted ? undefined : guest.audioStream?.getAudioTracks()[0];
          wanted.add(videoName);
          wanted.add(audioName);

          if (composed.get(videoName) !== videoTrack?.id) {
            if (composed.has(videoName)) client.removeVideoInputDevice(videoName);
            composed.delete(videoName);
            if (videoTrack) {
              await client.addVideoInputDevice(guest.videoStream, videoName, position);
              composed.set(videoName, videoTrack.id);
            }
          } else if (videoTrack) {
            client.updateVideoDeviceComposition(videoName, position);
          }
          if (composed.get(audioName) !== audioTrack?.id) {
            if (composed.has(audioName)) client.removeAudioInputDevice(audioName);
            composed.delete(audioName);
            if (audioTrack) {
              await client.addAudioInputDevice(guest.audioStream, audioName);
              composed.set(audioName, audioTrack.id);
            }
          }
        }
        composed.forEach((_, name) => {
          if (wanted.has(name)) return;
          if (name.startsWith("guest-video-")) client.removeVideoInputDevice(name);
          else client.removeAudioInputDevice(name);
          composed.delete(name);
        });

        const pausedSlot = paused ? JSON.stringify(slots[0]) : null;
        if (composed.get(PAUSED_IMAGE) !== pausedSlot) {
          if (composed.has(PAUSED_IMAGE)) client.removeImage(PAUSED_IMAGE);
          composed.delete(PAUSED_IMAGE);
          if (paused) {
            await client.addImageSource(pausedFrame(slots[0]), PAUSED_IMAGE, { index: 1, ...slots[0] });
            composed.set(PAUSED_IMAGE, pausedSlot);
          }
        }
      })
      .catch((error) => console.error("Stage composition error:", error));
  }, []);

  return {
    cameraStream,
    cameraError,
    startCamera,
    live,
    viewerCount,
    socket,
    goLive,
    stopLive,
    composeStage,
    pendingLive,
    resumeLive,
    discardLive,
    endedLive,
    sessionLimit,
  };
};

export default useLiveBroadcast;
